import mapboxgl, { ExpressionSpecification, GeoJSONSourceSpecification, SymbolLayerSpecification } from 'mapbox-gl'

import { hasLiveStyle, safeAddLayer, safeAddSource } from '../MapView/mapTeardown'
import { obstacleIcons } from './icons'

export const OBSTACLE_SOURCE_ID = 'obstacles'
export const MIN_OBSTACLE_ZOOM = 8.5
export const MIN_OBSTACLE_LABEL_ZOOM = 12
export const OBSTACLE_CLUSTER_ZOOM_BUMP = 1.5

export const OBSTACLE_ICON_SIZE: ExpressionSpecification = ['interpolate', ['linear'], ['zoom'], 7, 0.23, 9.5, 0.3]

export const OBSTACLE_LAYER_IDS = {
  icons: 'map-obstacles-icons-layer',
  clusters: 'map-obstacles-clusters-layer',
  selectedIcons: 'map-obstacles-selected-icons-layer',
  labels: 'map-obstacles-labels-layer'
} as const

export const OBSTACLE_LAYER_ORDER = [
  OBSTACLE_LAYER_IDS.icons,
  OBSTACLE_LAYER_IDS.clusters,
  OBSTACLE_LAYER_IDS.selectedIcons,
  OBSTACLE_LAYER_IDS.labels
] as const

const emptySelectedObstacleId = ''

const hasSelection = (selectedObstacleId: string): boolean => selectedObstacleId !== emptySelectedObstacleId

export const obstacleFilters = {
  icons: (selectedObstacleId = emptySelectedObstacleId): ExpressionSpecification => [
    'all',
    ['!', ['has', 'point_count']],
    ['!=', ['get', 'obstacleId'], selectedObstacleId]
  ],
  clusters: (): ExpressionSpecification => ['has', 'point_count'],
  selectedIcons: (selectedObstacleId = emptySelectedObstacleId): ExpressionSpecification => [
    'all',
    ['!', ['has', 'point_count']],
    ['==', ['get', 'obstacleId'], selectedObstacleId]
  ],
  labels: (selectedObstacleId = emptySelectedObstacleId): ExpressionSpecification =>
    hasSelection(selectedObstacleId)
      ? ['all', ['!', ['has', 'point_count']], ['==', ['get', 'obstacleId'], selectedObstacleId]]
      : ['!', ['has', 'point_count']]
}

export function createObstacleSourceSpecification(dataUrl: string): GeoJSONSourceSpecification {
  return {
    type: 'geojson',
    data: dataUrl,
    cluster: true,
    clusterRadius: 30,
    clusterProperties: {
      maxElevationTopFt: [
        ['max', ['accumulated'], ['get', 'maxElevationTopFt']],
        ['get', 'elevationTopFt']
      ]
    } as GeoJSONSourceSpecification['clusterProperties']
  }
}

export function createObstacleLayerSpecifications(selectedObstacleId?: string): SymbolLayerSpecification[] {
  return [
    {
      id: OBSTACLE_LAYER_IDS.icons,
      type: 'symbol',
      source: OBSTACLE_SOURCE_ID,
      minzoom: MIN_OBSTACLE_ZOOM,
      filter: obstacleFilters.icons(selectedObstacleId),
      layout: {
        'icon-image': ['get', 'icon'],
        'icon-size': OBSTACLE_ICON_SIZE,
        'icon-anchor': 'center',
        'icon-padding': 0,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-optional': false,
        'symbol-z-order': 'source',
        'symbol-sort-key': ['get', 'sortKey']
      }
    },
    {
      id: OBSTACLE_LAYER_IDS.clusters,
      type: 'symbol',
      source: OBSTACLE_SOURCE_ID,
      minzoom: MIN_OBSTACLE_ZOOM,
      filter: obstacleFilters.clusters(),
      layout: {
        'icon-image': 'obstacle-cluster',
        'icon-size': OBSTACLE_ICON_SIZE,
        'icon-anchor': 'center',
        'icon-padding': 0,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-optional': false,
        'symbol-z-order': 'source',
        'symbol-sort-key': 3
      }
    },
    {
      id: OBSTACLE_LAYER_IDS.selectedIcons,
      type: 'symbol',
      source: OBSTACLE_SOURCE_ID,
      minzoom: MIN_OBSTACLE_ZOOM,
      filter: obstacleFilters.selectedIcons(selectedObstacleId),
      layout: {
        'icon-image': ['get', 'selectedIcon'],
        'icon-size': OBSTACLE_ICON_SIZE,
        'icon-anchor': 'center',
        'icon-padding': 0,
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
        'icon-optional': false,
        'symbol-z-order': 'source',
        'symbol-sort-key': 4
      }
    },
    {
      id: OBSTACLE_LAYER_IDS.labels,
      type: 'symbol',
      source: OBSTACLE_SOURCE_ID,
      minzoom: MIN_OBSTACLE_LABEL_ZOOM,
      filter: obstacleFilters.labels(selectedObstacleId),
      layout: {
        'text-field': ['get', 'altitudeLabel'],
        'text-font': ['Open Sans Bold'],
        'text-size': 12,
        'text-anchor': 'center',
        'text-offset': [1.4, -1.8],
        'text-justify': 'center',
        'text-keep-upright': true,
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'symbol-z-order': 'source',
        'symbol-sort-key': ['coalesce', ['get', 'sortKey'], 0]
      },
      paint: {
        'text-color': '#fcfcfc',
        'text-halo-color': '#060809',
        'text-halo-width': 1.5,
        'text-halo-blur': 0.25
      }
    }
  ]
}

export function addObstacleSourceAndLayers(
  map: mapboxgl.Map,
  dataUrl: string,
  beforeLayerId?: string,
  selectedObstacleId?: string
): void {
  safeAddSource(map, OBSTACLE_SOURCE_ID, createObstacleSourceSpecification(dataUrl))

  createObstacleLayerSpecifications(selectedObstacleId).forEach((layer) => {
    safeAddLayer(map, layer, beforeLayerId)
  })
}

export function removeObstacleLayersAndSource(map: mapboxgl.Map): void {
  const layerRemovalOrder = [...OBSTACLE_LAYER_ORDER].reverse()

  layerRemovalOrder.forEach((layerId) => {
    if (map.getLayer(layerId)) {
      map.removeLayer(layerId)
    }
  })

  if (map.getSource(OBSTACLE_SOURCE_ID)) {
    map.removeSource(OBSTACLE_SOURCE_ID)
  }
}

export function setObstacleSelectionFilter(map: mapboxgl.Map, selectedObstacleId?: string): void {
  if (!hasLiveStyle(map)) return

  if (map.getLayer(OBSTACLE_LAYER_IDS.icons)) {
    map.setFilter(OBSTACLE_LAYER_IDS.icons, obstacleFilters.icons(selectedObstacleId))
  }

  if (map.getLayer(OBSTACLE_LAYER_IDS.clusters)) {
    map.setFilter(OBSTACLE_LAYER_IDS.clusters, obstacleFilters.clusters())
  }

  if (map.getLayer(OBSTACLE_LAYER_IDS.selectedIcons)) {
    map.setFilter(OBSTACLE_LAYER_IDS.selectedIcons, obstacleFilters.selectedIcons(selectedObstacleId))
  }

  if (map.getLayer(OBSTACLE_LAYER_IDS.labels)) {
    map.setFilter(OBSTACLE_LAYER_IDS.labels, obstacleFilters.labels(selectedObstacleId))
  }
}

export async function registerObstacleImages(map: mapboxgl.Map): Promise<void> {
  await Promise.all(
    Object.entries(obstacleIcons).map(([imageId, imageSrc]) => {
      return new Promise<void>((resolve, reject) => {
        if (map.hasImage(imageId)) {
          resolve()
          return
        }

        map.loadImage(imageSrc, (error, image) => {
          if (error) {
            reject(error)
            return
          }

          if (!image) {
            reject(new Error(`Could not load obstacle image ${imageId}`))
            return
          }

          // The style may have been reloaded while the image was loading.
          if (hasLiveStyle(map) && !map.hasImage(imageId)) {
            map.addImage(imageId, image)
          }

          resolve()
        })
      })
    })
  )
}
